// ============================================
// JOB DETAIL PAGE
// ============================================

import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  Edit, 
  Pause, 
  Play, 
  Trash2, 
  Users, 
  Calendar, 
  DollarSign,
  Clock,
  Ship,
  Briefcase,
  FileText,
  MoreVertical,
  Share2,
  Zap,
  Linkedin,
} from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { PageHeader, StatusBadge, EmptyState, DataTable, ConfirmDialog } from '@/components';
import { jobsApi } from '@/services/api';
import { useUpdateJobStatus, useMyCompany } from '@/hooks';
import toast from 'react-hot-toast';
import { 
  formatDate, 
  formatCurrency, 
  formatRelativeTime,
  jobStatusLabels, 
  departmentLabels, 
  vesselTypeLabels,
  applicationStatusLabels,
} from '@/utils';
import type { Column } from '@/components';

export const Route = createFileRoute('/_authenticated/jobs/$slug')({
  component: JobDetailPage,
});

function JobDetailPage() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();

  // Fetch job details
  const { data: rawJob, isLoading, refetch } = useQuery({
    queryKey: ['jobs', slug],
    queryFn: () => jobsApi.getBySlug(slug),
    enabled: !!slug,
  });

  // Handle backend response wrapper
  const job = (rawJob as any)?.data || rawJob;

  // Fetch applications
  const { data: rawApplications, isLoading: applicationsLoading } = useQuery({
    queryKey: ['applications', 'job', job?.id],
    queryFn: () => jobsApi.getApplications(job!.id),
    enabled: !!job?.id,
  });

  // Handle backend response wrapper for applications
  const applications = Array.isArray(rawApplications) 
    ? rawApplications 
    : (Array.isArray((rawApplications as any)?.data) 
        ? (rawApplications as any).data 
        : (Array.isArray((rawApplications as any)?.items) 
            ? (rawApplications as any).items 
            : []));

  const queryClient = useQueryClient();
  const { data: companyResponse } = useMyCompany();
  const company = (companyResponse as any)?.data || companyResponse;

  const [dopeConfirmOpen, setDopeConfirmOpen] = useState(false);

  const dopeJobMutation = useMutation({
    mutationFn: (id: string) => jobsApi.dope(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs', slug] });
      queryClient.invalidateQueries({ queryKey: ['company', 'me'] });
      setDopeConfirmOpen(false);
      toast.success('İlan başarıyla öne çıkarıldı!');
      refetch();
    },
    onError: (err: any) => {
      const errMsg = err?.response?.data?.message || err?.message || 'Bir hata oluştu';
      toast.error(errMsg);
    },
  });

  const updateStatusMutation = useUpdateJobStatus();

  const handleStatusChange = (status: string) => {
    if (!job) return;
    updateStatusMutation.mutate(
      { id: job.id, status },
      { onSuccess: () => refetch() }
    );
  };

  const handleShare = async () => {
    if (!job) return;
    const publicUrl = `https://crewatsea.com/jobs/${job.slug}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: job.title,
          text: `${job.title} - Crew At Sea ilanına göz atın.`,
          url: publicUrl,
        });
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          await navigator.clipboard.writeText(publicUrl);
          toast.success('İlan linki kopyalandı!');
        }
      }
    } else {
      await navigator.clipboard.writeText(publicUrl);
      toast.success('İlan linki kopyalandı!');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="bg-white rounded-maritime border border-gray-200 p-6">
          <div className="h-6 w-64 bg-gray-200 rounded animate-pulse mb-4" />
          <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <EmptyState
        icon={<Briefcase className="w-8 h-8 text-gray-400" />}
        title="İlan bulunamadı"
        description="Bu ilan mevcut değil veya silinmiş olabilir."
        action={
          <Link to="/jobs" className="btn-primary">
            İlanlara Dön
          </Link>
        }
      />
    );
  }

  const applicationColumns: Column<any>[] = [
    {
      key: 'seafarer',
      header: 'Aday',
      render: (app) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-maritime-100 flex items-center justify-center text-sm font-medium text-maritime-600">
            {app.seafarer?.firstName?.[0]}{app.seafarer?.lastName?.[0]}
          </div>
          <div>
            <p className="font-medium text-gray-900">
              {app.seafarer?.firstName} {app.seafarer?.lastName}
            </p>
            <p className="text-xs text-gray-500">{app.seafarer?.rank?.name}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'expectedSalary',
      header: 'Beklenen Maaş',
      render: (app) => (
        <span className="text-sm">
          {app.expectedSalary ? formatCurrency(app.expectedSalary) : '-'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Durum',
      render: (app) => (
        <StatusBadge
          status={app.status}
          label={applicationStatusLabels[app.status]}
        />
      ),
    },
    {
      key: 'createdAt',
      header: 'Tarih',
      render: (app) => (
        <span className="text-sm text-gray-500">
          {formatRelativeTime(app.createdAt)}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={job.title}
        description={`${departmentLabels[job.department] || job.department} • ${job.rank?.name || ``}`}
        actions={
          <div className="flex items-center gap-3">
            <Link to="/jobs" className="btn-secondary">
              <ArrowLeft className="w-4 h-4" />
              Geri
            </Link>
            <button onClick={handleShare} className="btn-secondary">
              <Share2 className="w-4 h-4" />
              Paylaş
            </button>
            <Link to="/jobs/$id/edit" params={{ id: job.id }} className="btn-secondary">
              <Edit className="w-4 h-4" />
              Düzenle
            </Link>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button className="btn-secondary">
                  <MoreVertical className="w-4 h-4" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className="min-w-[160px] bg-white rounded-maritime shadow-lg border border-gray-200 py-1 z-50"
                  sideOffset={5}
                  align="end"
                >
                  {job.status === 'ACTIVE' ? (
                    <>
                      {!job.isUrgent && (
                        <DropdownMenu.Item
                          className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer outline-none font-medium animate-pulse"
                          onSelect={() => setDopeConfirmOpen(true)}
                        >
                          <Zap className="w-4 h-4 text-red-500 fill-red-500" />
                          Öne Çıkar (Doping)
                        </DropdownMenu.Item>
                      )}
                      <DropdownMenu.Item
                        className="flex items-center gap-2 px-3 py-2 text-sm text-amber-600 hover:bg-amber-50 cursor-pointer outline-none"
                        onSelect={() => handleStatusChange('PAUSED')}
                      >
                        <Pause className="w-4 h-4" />
                        Duraklat
                      </DropdownMenu.Item>
                    </>
                  ) : job.status === 'PAUSED' ? (
                    <DropdownMenu.Item
                      className="flex items-center gap-2 px-3 py-2 text-sm text-green-600 hover:bg-green-50 cursor-pointer outline-none"
                      onSelect={() => handleStatusChange('ACTIVE')}
                    >
                      <Play className="w-4 h-4" />
                      Aktifleştir
                    </DropdownMenu.Item>
                  ) : null}
                  <DropdownMenu.Item
                    className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer outline-none"
                    onSelect={() => handleStatusChange('CLOSED')}
                  >
                    <Trash2 className="w-4 h-4" />
                    Kapat
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Doping Recommendation Banner */}
          {job.status === 'ACTIVE' && !job.isUrgent && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5 shadow-sm flex items-start justify-between gap-4">
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600 flex-shrink-0 mt-0.5 animate-pulse">
                  <Zap className="w-5 h-5 fill-amber-500 text-amber-500" />
                </div>
                <div>
                  <h4 className="font-bold text-amber-900 text-sm">İlanı Dopingleyip Başvuruları Artırın!</h4>
                  <p className="text-xs text-amber-700/90 mt-1 leading-relaxed">
                    Bu ilanı dopingleyerek 24 saat boyunca en üste sabitleyebilir ve arama sonuçlarında "ACİL" rozetiyle daha hızlı başvuru toplayabilirsiniz.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setDopeConfirmOpen(true)}
                className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-4 py-2 rounded-xl transition-all shadow-sm shrink-0 flex items-center gap-1.5 cursor-pointer"
              >
                <Zap className="w-3.5 h-3.5 fill-current" />
                Doping Uygula
              </button>
            </div>
          )}

          {/* Job Info */}
          <div className="bg-white rounded-maritime border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">İlan Detayları</h3>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleShare}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-maritime-700 bg-maritime-50 hover:bg-maritime-100 transition-all duration-200"
                >
                  <Share2 className="w-3.5 h-3.5" />
                  İlanı Paylaş
                </button>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://crewatsea.com/jobs/${job.slug}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-white bg-[#0077b5] hover:bg-[#006297] transition-all duration-200"
                >
                  <Linkedin className="w-3.5 h-3.5 fill-current" />
                  LinkedIn'de Paylaş
                </a>
                <StatusBadge status={job.status} label={jobStatusLabels[job.status]} />
              </div>
            </div>
            
            <div className="prose prose-sm max-w-none text-gray-600 break-words overflow-hidden">
              <p className="whitespace-pre-wrap break-words">{job.description}</p>
            </div>

            {Array.isArray(job.requirements) && job.requirements.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Gereksinimler</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                  {job.requirements.map((req: any, i: number) => (
                    <li key={i}>
                      {typeof req === 'string' 
                        ? req 
                        : req?.certificateType?.nameTr || req?.certificateType?.nameEn || req?.certificateType?.name || req?.nameTr || req?.nameEn || req?.name || '-'}
                      {typeof req === 'object' && req?.isRequired && (
                        <span className="ml-1 text-red-500 text-xs">*</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {Array.isArray(job.requiredCertificates) && job.requiredCertificates.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Gerekli Sertifikalar</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                  {job.requiredCertificates.map((cert: any, i: number) => (
                    <li key={i}>
                      {cert?.certificateType?.nameTr || cert?.certificateType?.nameEn || cert?.certificateType?.name || cert?.nameTr || cert?.nameEn || cert?.name || '-'}
                      {cert?.isRequired && (
                        <span className="ml-1 text-red-500 text-xs">(Zorunlu)</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Applications */}
          <div className="bg-white rounded-maritime border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Başvurular ({applications?.length || 0})
              </h3>
            </div>
            <DataTable
              columns={applicationColumns}
              data={applications || []}
              keyExtractor={(app) => app.id}
              loading={applicationsLoading}
              emptyState={{
                icon: <FileText className="w-8 h-8 text-gray-400" />,
                title: 'Henüz başvuru yok',
                description: 'Bu ilana henüz başvuru yapılmamış.',
              }}
              onRowClick={(app) => navigate({ to: `/applications/${app.id}` })}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white rounded-maritime border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Özet</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-600">
                  <Users className="w-4 h-4" />
                  <span className="text-sm">Başvuru Sayısı</span>
                </div>
                <span className="font-semibold">{job._count?.applications || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">Yayınlanma</span>
                </div>
                <span className="text-sm">{formatDate(job.createdAt)}</span>
              </div>
              {job.expiresAt && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">Bitiş</span>
                  </div>
                  <span className="text-sm">{formatDate(job.expiresAt)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Job Details */}
          <div className="bg-white rounded-maritime border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Detaylar</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-600">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-sm">Maaş</span>
                </div>
                <span className="text-sm font-medium">
                  {formatCurrency(job.salaryMin)} - {formatCurrency(job.salaryMax)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">Kontrat</span>
                </div>
                <span className="text-sm">{job.contractDuration} ay</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-600">
                  <Ship className="w-4 h-4" />
                  <span className="text-sm">Gemi Tipi</span>
                </div>
                <span className="text-sm">{vesselTypeLabels[job.vesselType] || job.vesselType}</span>
              </div>
              {job.vesselName && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Ship className="w-4 h-4" />
                    <span className="text-sm">Gemi Adı</span>
                  </div>
                  <span className="text-sm">{job.vesselName}</span>
                </div>
              )}
              {job.experienceMin > 0 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Briefcase className="w-4 h-4" />
                    <span className="text-sm">Min. Deneyim</span>
                  </div>
                  <span className="text-sm">{job.experienceMin} yıl</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={dopeConfirmOpen}
        onOpenChange={setDopeConfirmOpen}
        title="İlanı Öne Çıkar (Doping)"
        description={`"${job?.title}" ilanını öne çıkarmak (24 saatliğine sabitlemek) istediğinize emin misiniz? Bu işlem 1 Acil İlan Kredinizi tüketecektir. Kalan Acil İlan Krediniz: ${company?.urgentJobCredits || 0}`}
        variant="success"
        confirmText="Öne Çıkar (1 Kredi)"
        onConfirm={() => dopeJobMutation.mutate(job.id)}
        loading={dopeJobMutation.isPending}
      />
    </div>
  );
}
